// Isacord thread-colour picker.
//
//   max === 1 : single-pick (replace on tap). Used everywhere now — text
//               is one colour, and a design is either colour-matched or
//               stitched in one colour.
//   max  >  1 : legacy multi-pick up to `max`. Kept for completeness.
//
// When `matchOption` is set, a full-width "Match the design" choice sits
// above the swatch grid. Picking it stores the MATCH_DESIGN_CODE sentinel
// so downstream code (cart properties, summaries) knows the embroidery
// team should colour-match the artwork rather than use a fixed swatch.

import type { ThreadColor } from "../types";

/** Sentinel stored in a thread-code array to mean "colour-match the
 *  uploaded artwork at digitising" rather than a specific Isacord code. */
export const MATCH_DESIGN_CODE = "match-design";

export function ThreadPicker({
  threads,
  value,
  onChange,
  max,
  heading,
  hint,
  emptyHint,
  matchOption,
  matchHintExtra,
  matchPriceCents,
}: {
  threads: ThreadColor[];
  value: string[];
  onChange: (next: string[]) => void;
  max: number;
  heading: string;
  /** Optional explanatory line shown above the chip summary. */
  hint?: string;
  /** Copy shown next to the chip row when nothing is picked yet. */
  emptyHint?: string;
  /** When true, show the "Match the design" choice above the grid. */
  matchOption?: boolean;
  /** Pet-photo trap (2026-07-12): extra clause appended to the "Match
   *  the design" sub-line. Used on logo-flow spots whose upload was
   *  classified photo-like but NOT upgraded to a Portrait, so the
   *  customer isn't led to expect a full-colour photo reproduction. */
  matchHintExtra?: string;
  /** 2026-08-14: cents the "Match the design" choice ADDS on this spot.
   *  Shown as "+$45" on the tile so the fee is never a checkout
   *  surprise. Omit when the spot's style toggle already covers it. */
  matchPriceCents?: number;
}) {
  const selected = value || [];
  const matchSelected = selected.includes(MATCH_DESIGN_CODE);
  // Real Isacord codes only — the sentinel is handled separately.
  const colourSelected = selected.filter((c) => c !== MATCH_DESIGN_CODE);

  const toggle = (code: string) => {
    if (selected.includes(code)) {
      // Tap-to-remove always works.
      onChange(selected.filter((c) => c !== code));
      return;
    }
    if (max === 1) {
      // Single-pick mode: replace whatever was there (incl. the sentinel).
      onChange([code]);
      return;
    }
    if (selected.length >= max) return; // full, ignore
    onChange([...selected.filter((c) => c !== MATCH_DESIGN_CODE), code]);
  };

  const counter = matchOption
    ? matchSelected
      ? "Colour-matched"
      : colourSelected.length > 0
        ? "1 colour picked"
        : "Choose an option"
    : max === 1
      ? selected.length === 0
        ? "No thread picked"
        : "1 thread picked"
      : `Selected ${selected.length} of ${max}`;

  return (
    <div className="rk-pc-threads">
      <div className="rk-pc-threads__head">
        <span className="rk-pc-threads__heading">{heading}</span>
        <span className="rk-pc-threads__counter">{counter}</span>
      </div>

      {hint ? <p className="rk-pc-threads__hint">{hint}</p> : null}

      {matchOption ? (
        <>
          <button
            type="button"
            className={`rk-pc-thread-match${matchSelected ? " is-selected" : ""}`}
            onClick={() => toggle(MATCH_DESIGN_CODE)}
            aria-pressed={matchSelected}
          >
            <span className="rk-pc-thread-match__check" aria-hidden="true">
              {matchSelected ? "✓" : "✦"}
            </span>
            <span className="rk-pc-thread-match__body">
              <span className="rk-pc-thread-match__title">
                Match the design
                {matchPriceCents
                  ? ` (+$${(matchPriceCents / 100).toFixed(0)})`
                  : ""}
              </span>
              <span className="rk-pc-thread-match__sub">
                We'll colour-match your artwork to the closest Isacord
                threads when we digitise it.
                {matchPriceCents
                  ? " Colour-matched stitching is the coloured + filled upgrade."
                  : ""}
                {matchHintExtra ? ` ${matchHintExtra}` : ""}
              </span>
            </span>
          </button>
          <div className="rk-pc-thread-or">
            <span>or stitch it all in one colour</span>
          </div>
        </>
      ) : null}

      {!matchSelected ? (
        <div className="rk-pc-threads__chips">
          {colourSelected.length === 0 ? (
            <span className="rk-pc-threads__empty">
              {emptyHint ?? "Tap a swatch below to pick a colour."}
            </span>
          ) : (
            colourSelected.map((code) => {
              const t = threads.find((x) => x.code === code);
              if (!t) return null;
              return (
                <span key={code} className="rk-pc-thread-chip">
                  <span
                    className="rk-pc-thread-chip__swatch"
                    style={{ background: t.hex }}
                  />
                  <span className="rk-pc-thread-chip__name">{t.name}</span>
                  <span className="rk-pc-thread-chip__code">{t.code}</span>
                  <button
                    type="button"
                    className="rk-pc-thread-chip__x"
                    onClick={() => toggle(code)}
                    aria-label={`Remove ${t.name}`}
                  >
                    ×
                  </button>
                </span>
              );
            })
          )}
        </div>
      ) : null}

      <div className="rk-pc-thread-grid">
        {threads.map((t) => {
          const sel = selected.includes(t.code);
          const disabled = max > 1 && !sel && colourSelected.length >= max;
          return (
            <button
              type="button"
              key={t.id}
              className={`rk-pc-thread-dot${sel ? " is-selected" : ""}`}
              disabled={disabled}
              onClick={() => toggle(t.code)}
              title={`${t.name} · Isacord ${t.code}`}
              aria-pressed={sel}
              aria-label={`${t.name}, Isacord ${t.code}`}
              style={{ background: t.hex }}
            />
          );
        })}
      </div>

      {!matchOption ? (
        <p className="rk-pc-threads__foot">
          {max > 1
            ? `Limited to ${max} threads. More colours mean more thread changes, longer stitch time, less crispness.`
            : "Text uses a single thread colour. Tap another swatch to swap."}
        </p>
      ) : null}
    </div>
  );
}
